/**
 * useApprovalCenter — TanStack Query v5 hooks
 * queryKey 패턴: ['approval-center', '<resource>', ...params]
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { approvalCenterApi } from '../api/approvalCenterApi';
import type { ApprovalSearchCondition, CreateApprovalRequest, ApprovalDecisionRequest } from '../types/approvalCenter';

// ─── 목록 조회 (운영 목록 — APPROVAL_READ) ───────────────────────────────────
// enabled — 권한 조건부 쿼리 게이트(permission-first): 탭 UI 숨김과 동시에 쿼리 실행도 가드.
// 기본 true 로 기존 호출부 하위호환(#648 §R648-2).
export function useApprovalInbox(params: ApprovalSearchCondition, enabled = true) {
  return useQuery({
    queryKey: ['approval-center', 'list', params],
    queryFn: () => approvalCenterApi.list(params),
    enabled,
    staleTime: 30_000,
  });
}

// ─── 내가 올린 결재 ───────────────────────────────────────────────────────────
export function useMyRequests(params: Omit<ApprovalSearchCondition, 'requester'>) {
  return useQuery({
    queryKey: ['approval-center', 'mine', params],
    queryFn: () => approvalCenterApi.mine(params),
    staleTime: 30_000,
  });
}

// ─── 받은 결재 (역할 기반 태스크 라우팅 — C068 listMyTasks) ──────────────────
export function useMyTasks() {
  return useQuery({
    queryKey: ['approval-center', 'tasks', 'mine'],
    queryFn: () => approvalCenterApi.myTasks(),
    staleTime: 20_000,
  });
}

// ─── 결재 상세 ────────────────────────────────────────────────────────────────
export function useApprovalRequest(id: number | null) {
  return useQuery({
    queryKey: ['approval-center', 'detail', id],
    queryFn: () => approvalCenterApi.get(id!),
    enabled: id !== null,
    staleTime: 15_000,
  });
}

// ─── 결재 양식 목록 ───────────────────────────────────────────────────────────
export function useApprovalForms() {
  return useQuery({
    queryKey: ['approval-center', 'forms'],
    queryFn: () => approvalCenterApi.forms(),
    staleTime: 60_000,
  });
}

// ─── 결재 상신 ────────────────────────────────────────────────────────────────
export function useCreateApproval() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateApprovalRequest) => approvalCenterApi.create(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['approval-center', 'mine'] });
      void queryClient.invalidateQueries({ queryKey: ['approval-center', 'list'] });
    },
  });
}

// ─── 승인 ─────────────────────────────────────────────────────────────────────
export function useApprove(requestId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ApprovalDecisionRequest) => approvalCenterApi.approve(requestId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['approval-center', 'detail', requestId] });
      void queryClient.invalidateQueries({ queryKey: ['approval-center', 'tasks', 'mine'] });
      void queryClient.invalidateQueries({ queryKey: ['approval-center', 'mine'] });
      void queryClient.invalidateQueries({ queryKey: ['approval-center', 'list'] });
    },
  });
}

// ─── 반려 ─────────────────────────────────────────────────────────────────────
export function useReject(requestId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ApprovalDecisionRequest) => approvalCenterApi.reject(requestId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['approval-center', 'detail', requestId] });
      void queryClient.invalidateQueries({ queryKey: ['approval-center', 'tasks', 'mine'] });
      void queryClient.invalidateQueries({ queryKey: ['approval-center', 'mine'] });
      void queryClient.invalidateQueries({ queryKey: ['approval-center', 'list'] });
    },
  });
}
